<i18n>
{
  "en": {
    "columns": "Number of columns",
    "contain": "Fit",
    "cover": "Cover",
    "fileFormatError": "This format is not valid",
    "fileSizeError": "Image is too big. Maximum size is 2 MB.",
    "gridConfiguration": "Grid structure configuration",
    "image": "Select background image",
    "aspectRatio": "Aspect ratio",
    "imageSizeBehaviour": "Image size behaviour",
    "invalidNumberError": "Invalid number",
    "paddingGrid": "Grid structure margin",
    "rows": "Number of rows"
  },
  "es": {
    "columns": "Número de columnas",
    "contain": "Encajar",
    "cover": "Cubrir",
    "fileFormatError": "Este formato no es valido",
    "fileSizeError": "La imagen es demasiado grande. El tamaño máximo es 2 MB.",
    "gridConfiguration": "Configuración de la estructura",
    "image": "Seleccione imagen de fondo",
    "aspectRatio": "Ratio de aspecto",
    "imageSizeBehaviour": "Comportamiento de la imagen de fondo",
    "invalidNumberError": "Número no válido",
    "paddingGrid": "Margen de la estructura",
    "rows": "Número de filas"
  }
}
</i18n>
<template>
  <div class="m-4 mt-3 d-flex justify-content-center flex-column align-center grid-form">
    <v-form>
      <div class="title-form-grid">{{ $t('gridConfiguration') }}</div>
      <div class="d-flex">
        <v-text-field type="number" outlined :value="col" @input="handleChangeCol"
                      :rules="noValidNumberRule" data-testid="col-input" :label="$t('columns')"
                      class="required mx-1"/>
        <v-text-field type="number" outlined :value="row" @input="handleChangeRow"
                      :rules="noValidNumberRule" data-testid="row-input" :label="$t('rows')"
                      class="required mx-1"/>
      </div>
      <div class="d-flex">
          <v-text-field type="number" outlined :value="paddingGrid" @input="handleChangePaddingGrid"
                :rules="noValidNumberRule" data-testid="pad-input" :label="$t('paddingGrid')"
                class="mx-1"/>
          <v-select :items="aspectsRatio" v-model="aspectRatio"
            :label="$t('aspectRatio')" outlined @change="handleChangeRatio"
            item-value="value" item-text="name" data-testid="justify-input" class="mx-1"/>
      </div>
      <v-file-input outlined :value="image" data-testid="img-input" @change="handleChangeImage"
                    :rules="imageRules"
                    :show-size="showImageSize"
                    accept="image/png, image/jpeg, image/bmp" prepend-inner-icon="mdi-camera"
                    prepend-icon="" :label="$t('image')" class="mb-0"/>
      <v-row v-if="showImageConfigFields" class="mx-0 w-100">
        <v-col cols="6">
          <v-select :items="imageSizeBehaviour" v-model="backgroundImageSizeBehaviour"
                    :label="$t('imageSizeBehaviour')" outlined @change="handleChangeBehaviour"
                    item-value="value" item-text="name" data-testid="aligh-input" class="ml-2"/>
        </v-col>
      </v-row>
    </v-form>
    <grid-item-list :itemRequired="itemRequired"/>
    <GridItemForm v-if="selectedGridItemConfiguration"/>
  </div>
</template>
<script>
import { Components, MapActions, MapGetters } from '@core/helpers/decorators';
import Component from 'vue-class-component';
import Vue from 'vue';
import {
  imageFileType,
  maxSize,
  notNumberInBetween,
  notValidNumber
} from '@core/helpers/formValidators';
import { Prop } from 'vue-property-decorator';
import GridItemForm from './gridItemForm.vue';
import GridItemList from './gridItemList.vue';

@Component
@Components({ GridItemForm, GridItemList })
@MapGetters(['col', 'row', 'paddingGrid', 'gridConfiguration', 'image', 'aspectRatio', 'backgroundImageSizeBehaviour', 'selectedGridItemConfiguration'])
@MapActions(['selectCol', 'selectRow', 'selectPaddingGrid', 'selectImage', 'selectAspectRatio', 'selectBackgroundImageSizeBehaviour'])
export default class GridForm extends Vue {
  @Prop({ Array, default: () => [] }) itemRequired;

  get noValidNumberRule() {
    return [notValidNumber(this.$t('invalidNumberError')), notNumberInBetween(this.$t('invalidNumberError'), 1, 10)];
  }

  get imageRules() {
    return [maxSize(this.$t('fileSizeError'), 2000000), imageFileType(this.$t('fileFormatError'))];
  }

  get showImageSize() {
    return this.image instanceof File && this.image.size > 0;
  }

  get showImageConfigFields() {
    return this.image;
  }

  get aspectsRatio() {
    return [
      { name: '16 / 9', value: '16 / 9' },
      { name: '9 / 16', value: '9 / 16' },
      { name: '4 / 3', value: '4 / 3' },
      { name: '3 / 2', value: '3 / 2' },
      { name: '21 / 9', value: '21 / 9' },
      { name: '1.85 / 11', value: '1.85 / 11' },
    ];
  }

  get imageSizeBehaviour() {
    return [
      { name: this.$t('contain'), value: 'contain' },
      { name: this.$t('cover'), value: 'cover' },
    ];
  }

  handleChangeCol(value) {
    if (value > 0 && value <= 20) {
      this.selectCol(value);
      this.column = value;
    }
  }

  handleChangeRow(value) {
    if (value > 0 && value <= 20) {
      this.selectRow(value);
    }
  }

  handleChangePaddingGrid(value) {
    this.selectPaddingGrid(value);
  }

  handleChangeImage(value) {
    this.selectImage(value);
  }

  handleChangeRatio(value) {
    this.selectAspectRatio(value);
  }

  handleChangeBehaviour(value) {
    this.selectBackgroundImageSizeBehaviour(value);
  }
}

</script>
<style>
.title-form-grid {
  font-size: 1.25rem;
  font-weight: 500;
  letter-spacing: 0.0125em;
  line-height: 2rem;
  word-break: break-all;
  margin-bottom: 8px;
  text-align: center;
}

.grid-form {
  max-width: 440px;
  overflow-y: scroll;
  height: 80vh;
  padding: 10px;
}
</style>
